/**
 * Search Filters Modal Component
 *
 * Airbnb-style comprehensive filters modal for secondary search filters.
 * Manages: Goals, Interests, Home Country, Keywords, Username. "Clear all"
 * also turns off the quick filter chips outside the modal.
 */

import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';
import { clearQuickChips, resetSearchPreferences } from './search-quick-chips.js';

export function searchFiltersModal() {
    return {
        isOpen: false,
        // Preference reset from "Clear all". The search submit rewrites the same settings row, so it waits.
        pendingPersist: Promise.resolve(),
        preferencesResetSent: false,

        open() {
            this.isOpen = true;
            lockBodyScroll();
            this.$nextTick(() => {
                if (window.LucideIcons) {
                    window.LucideIcons.initialize(this.$el);
                }
            });
        },

        close() {
            this.isOpen = false;
            unlockBodyScroll();
        },

        apply() {
            this.close();

            const form = document.querySelector('.search-form');
            if (form) {
                // Sync gender checkboxes from modal to form hidden inputs
                this.syncGenderInputs();

                const offsetInput = form.querySelector('input[name="offset"]');
                if (offsetInput) offsetInput.value = '0';
                const pageInput = form.querySelector('input[name="page"]');
                if (pageInput) pageInput.value = '1';

                // Show loading skeletons at results level during page navigation
                const viewMode = form.querySelector('input[name="viewMode"]')?.value || 'list';
                if (window.showSearchLoadingState) {
                    window.showSearchLoadingState(viewMode);
                }

                this.pendingPersist.then(() => form.submit());
            }
        },

        syncGenderInputs() {
            // Gender checkboxes in age_gender dropdown need to sync to the hidden sex[] inputs
            const maleCheckbox = document.querySelector('.gender-checkbox[data-gender="male"]');
            const femaleCheckbox = document.querySelector('.gender-checkbox[data-gender="female"]');
            const maleInput = document.getElementById('sex-male-input');
            const femaleInput = document.getElementById('sex-female-input');

            if (maleCheckbox && maleInput) {
                maleInput.disabled = !maleCheckbox.checked;
            }
            if (femaleCheckbox && femaleInput) {
                femaleInput.disabled = !femaleCheckbox.checked;
            }
        },

        clearModalFilters() {
            const modal = this.$root;

            // Reset goals and goal exclusions — click active chips to toggle them off
            modal.querySelectorAll('input[name="lfor[]"], input[name="lfor_not[]"]').forEach(cb => {
                if (cb.checked) {
                    const chip = cb.closest('[x-data]');
                    if (chip) chip.click();
                }
            });

            // Clear interests via Alpine state
            const interestsEl = modal.querySelector('[x-data*="searchInterestsFilter"]');
            if (interestsEl && interestsEl._x_dataStack) {
                interestsEl._x_dataStack[0].selectedTags = [];
            }

            // Clear home country hidden inputs + Alpine state
            modal.querySelectorAll('input[name="home_countries[]"]').forEach(el => el.remove());
            const homeCountryEl = modal.querySelector('[x-data*="countryChips"]');
            if (homeCountryEl && homeCountryEl._x_dataStack) {
                homeCountryEl._x_dataStack[0].countries = [];
            }

            // Clear keywords
            const keywordsInput = modal.querySelector('input[name="keywords"]');
            if (keywordsInput) keywordsInput.value = '';

            // Clear username
            const usernameInput = modal.querySelector('input[name="username"]');
            if (usernameInput) usernameInput.value = '';

            const form = document.querySelector('.search-form');
            if (form) {
                // One reset clears both preferences, so a repeated press sends no second request.
                if (clearQuickChips(form) && !this.preferencesResetSent) {
                    this.preferencesResetSent = true;
                    this.pendingPersist = resetSearchPreferences().then(confirmed => {
                        if (!confirmed) this.preferencesResetSent = false;
                    });
                }
            }
        },

        init() {
            // Listen for open-filters-modal event
            document.addEventListener('open-filters-modal', () => {
                this.open();
            });
        }
    };
}
